import { useEffect, useRef, useState } from 'react'
import { type AdLocationKey } from '#app/ads/constants.ts'
import { type AdForRender } from '#app/utils/ads.server.ts'
import { AdBanner } from './ad-banner.tsx'

interface AdSlotProps {
	ad: AdForRender | null
	locationKey: AdLocationKey
	className?: string
}

/**
 * Ad slot component with impression tracking
 *
 * Tracks impression when ad becomes visible using IntersectionObserver.
 *
 * The impression is reported exactly once, via a same-origin beacon. The server
 * route is what writes to both AdMetricDay and PostHog — sending a PostHog event
 * from here as well would double every impression, and an advertiser's CTR is
 * a number we invoice against.
 */
export function AdSlot({ ad, locationKey, className }: AdSlotProps) {
	const [hasTracked, setHasTracked] = useState(false)
	const adRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!ad || hasTracked) return

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting && !hasTracked) {
						setHasTracked(true)

						// Send beacon to server for first-party tracking
						const formData = new FormData()
						formData.append('adId', ad.id)
						formData.append('locationKey', locationKey)

						// Try sendBeacon first (non-blocking), fallback to fetch
						if (navigator.sendBeacon) {
							const success = navigator.sendBeacon(
								'/resources/ad-impression',
								formData,
							)
							if (!success) {
								// Fallback to fetch if sendBeacon fails
								fetch('/resources/ad-impression', {
									method: 'POST',
									body: formData,
								}).catch((error) => {
									console.error('Failed to track ad impression:', error)
								})
							}
						} else {
							// Older browsers without sendBeacon
							fetch('/resources/ad-impression', {
								method: 'POST',
								body: formData,
							}).catch((error) => {
								console.error('Failed to track ad impression:', error)
							})
						}
					}
				})
			},
			{
				threshold: 0.5, // Track when 50% visible
				rootMargin: '0px',
			},
		)

		if (adRef.current) {
			observer.observe(adRef.current)
		}

		return () => {
			observer.disconnect()
		}
	}, [ad, hasTracked, locationKey])

	if (!ad) return null

	return (
		<div ref={adRef} className={className}>
			<AdBanner ad={ad} />
		</div>
	)
}
